import { useState } from "react";
import { Stack, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import DangerButton from "@quest_editor/components/Buttons/DangerButton";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { usePromotion, useQuests } from "@quest_editor/mocks";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import { getBuildOptionsDisplay } from "@quest_editor/utils/view/buildOptionsDisplay";
import PromotionInfoSection from "./PromotionInfoSection";
import QuestTableSection from "./QuestTableSection";

const PromotionDetail = () => {
  const { t } = useTranslation("questEditor");
  const { id: promotionId } = useParams<{ id: string }>();

  const { data: promotionData } = usePromotion(promotionId);
  const promotion = promotionData?.promotion;

  const { data: questsData } = useQuests({ promotionId });
  const quests = questsData?.quests ?? [];

  const { getTypeLabel } = getPromotionLabels();
  const { buildOptionsDisplay } = getBuildOptionsDisplay();

  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const pageCount = Math.max(1, Math.ceil(quests.length / rowsPerPage));
  const start = page * rowsPerPage;
  const pageRows = quests.slice(start, start + rowsPerPage);

  const handleCopyUrl = () => {
    if (promotion?.returnUrl) {
      navigator.clipboard.writeText(promotion.returnUrl);
    }
  };

  if (!promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1}>
          <SubActionButton
            startIcon={<FileCopyIcon />}
            component={Link}
            to="duplication"
          >
            {t("promotion_detail.button.duplication")}
          </SubActionButton>
          <DangerButton
            startIcon={<DeleteIcon />}
          >
            {t("promotion_detail.button.delete")}
          </DangerButton>
        </Stack>
      </Stack>

      <PromotionInfoSection
        promotion={promotion}
        getTypeLabel={getTypeLabel}
        buildOptionsDisplay={buildOptionsDisplay}
        onCopyUrl={handleCopyUrl}
      />

      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ width: "100%" }}
      >
        <SubActionButton
          component={Link}
          to="quest/register"
        >
          {t("promotion_detail.button.new")}
        </SubActionButton>
      </Stack>

      <QuestTableSection
        pageRows={pageRows}
        totalCount={quests.length}
        page={page}
        setPage={setPage}
        pageCount={pageCount}
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        pt={2}
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to="/quest_editor"
          sx={{ position: "absolute", left: 0 }}
        >
          {t("promotion_detail.button.back")}
        </BackButton>
        <Stack direction="row" spacing={2}>
          <ActionButton>
            {t("promotion_detail.button.goto_estimate")}
          </ActionButton>
          <ActionButton>
            {t("promotion_detail.button.confirm_order")}
          </ActionButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PromotionDetail;
